// src/api/orders.ts
import { opsHttp } from "../auth/apiClient.ts";
import type { Id } from "./_opResult.ts";
import type {
  ExchangeOrderDto,
  ExchangeOrderListItemDto,
  UpdateOrderRequest,
  PagedResult,
} from "./dto.ts";
import { opOk, opError } from "./_opResult.ts";
/** Мои ордера (из бэка: /api/Orders/by-worker/{id}) */
export async function getOrdersByWorker<T = ExchangeOrderDto>(workerId: Id): Promise<T[]> {
  const { data } = await opsHttp.get<T[]>(`/api/Orders/by-worker/${workerId}`);
  return data ?? [];
}

/** Удалить ордер */
export async function deleteOrder(id: Id): Promise<boolean> {
  const res = await opsHttp.delete(`/api/Orders/remove/${id}`);
  return res.status >= 200 && res.status < 300;
}
export async function patchOrderComment(
  orderId: Id,
  comment: string | null
): Promise<{ ok: true; unchanged?: boolean } | { ok: false; error: string }> {
  try {
    const { data } = await opsHttp.patch(`/api/Orders/${orderId}/comment`, { comment });
    // бек возвращает OperationResult/Ok(result). Поддержим обе схемы:
    if (opOk(data) || data?.success || data?.Success) {
      const v = data?.value ?? data?.Value ?? {};
      return { ok: true, unchanged: !!(v?.Unchanged ?? v?.unchanged) };
    }
    return { ok: false, error: opError(data) || "Ошибка сохранения комментария" };
  } catch (err: any) {
    const d = err?.response?.data;
    return { ok: false, error: opError(d) || "Сеть/сервер недоступны" };
  }
}
/** Обновить ордер (payload = тело UpdateOrderRequest в PascalCase) */
export async function updateOrder(
  orderId: Id,
  payload: UpdateOrderRequest
): Promise<{ ok: true } | { ok: false; error: string }> {
  try {
    const { data } = await opsHttp.put(`/api/Orders/${orderId}`, payload);
    // контроллер возвращает Ok(result) с полем Success/ErrorMessage
    return opOk(data) ? { ok: true } : { ok: false, error: opError(data) };
  } catch (err: any) {
    const d = err?.response?.data;
    return { ok: false, error: opError(d) || "Сеть/сервер недоступны" };
  }
}

/** Мои ордера постранично */
export async function getMyOrdersPaged<T = ExchangeOrderListItemDto>(
  skip = 0,
  take = 25
): Promise<PagedResult<T>> {
  const { data } = await opsHttp.get<PagedResult<T>>(`/api/Orders/my/paged`, {
    params: { skip, take },
  });
  return (
    data ?? {
      items: [],
      hasMore: false,
      skip,
      take,
    }
  );
}
export async function searchMyOrdersPaged<T = ExchangeOrderListItemDto>(
  q: string,
  skip = 0,
  take = 25
): Promise<PagedResult<T>> {
  if (!q?.trim()) return { items: [], hasMore: false };

  const { data } = await opsHttp.get<PagedResult<T>>(`/api/Orders/my/search`, {
    params: { q, skip, take },
  });

  return {
    items: (data?.items ?? []) as T[],
    hasMore: !!data?.hasMore,
  };
}

/** Закреплённые ордера */
export async function getMyPinnedOrders<T = ExchangeOrderDto>(workerId: Id): Promise<T[]> {
  const { data } = await opsHttp.get<T[]>(`/api/pins/by-worker/${workerId}/orders`);
  return data ?? [];
}
